import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { generateClient } from 'aws-amplify/api';
import { CurrentUser, UserRole } from '../types/admin';
import { GET_CURRENT_USER } from '../graphql/admin';

/**
 * Auth Context for managing current user state
 * Provides current user role and tenantId to admin interfaces
 * Handles authorization errors gracefully
 * Requirements: 10.5, 11.5, 12.3
 */
interface AuthContextType {
  currentUser: CurrentUser | null;
  loading: boolean;
  error: string | null;
  isAuthorizationError: boolean;
  refetch: () => Promise<void>;
  // Helper methods for role checking
  isPlatformAdmin: boolean;
  isTenantAdmin: boolean;
  isUser: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Helper to detect authorization errors from error messages
const isAuthError = (errorMessage: string): boolean => {
  const authErrorPatterns = [
    'unauthorized',
    'forbidden',
    'not found',
    'access denied',
    'permission denied',
    'not authenticated',
    'invalid token'
  ];
  const lowerError = errorMessage.toLowerCase();
  return authErrorPatterns.some(pattern => lowerError.includes(pattern));
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthorizationError, setIsAuthorizationError] = useState(false);

  const fetchCurrentUser = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsAuthorizationError(false);

      // Check if we're in preview mode
      const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                           !import.meta.env.VITE_COGNITO_USER_POOL_ID;

      if (isPreviewMode) {
        // Mock user for preview mode - default to Platform Admin for testing
        setCurrentUser({
          userId: 'preview-user-id',
          tenantId: null,
          role: UserRole.PLATFORM_ADMIN,
          email: 'preview@clairvoya.com',
          name: 'Preview User'
        });
        return;
      }

      const client = generateClient();
      let response: any;
      try {
        response = await client.graphql({
          query: GET_CURRENT_USER
        });
      } catch (graphqlErr: any) {
        // Amplify throws when there are GraphQL errors — extract the actual error
        response = graphqlErr;
      }

      if (response?.data?.getCurrentUser) {
        const user = response.data.getCurrentUser;
        setCurrentUser({
          userId: user.userId,
          tenantId: user.tenantId,
          role: user.role as UserRole,
          email: user.email,
          name: user.name
        });
      } else if (response?.errors && response.errors.length > 0) {
        // Handle GraphQL errors — log but don't crash the app
        const errorMessage = response.errors[0].message || 'Failed to fetch user';
        console.warn('getCurrentUser GraphQL error:', JSON.stringify(response.errors, null, 2));
        setError(errorMessage);
        setIsAuthorizationError(isAuthError(errorMessage));
      }
      // If getCurrentUser returns null with no errors, user simply isn't in DB yet — stay null
    } catch (err: any) {
      console.error('Failed to fetch current user:', err);
      const errorMessage = err.message || 'Failed to fetch user';
      setError(errorMessage);
      setIsAuthorizationError(isAuthError(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  // Compute role helper flags
  const isPlatformAdmin = currentUser?.role === UserRole.PLATFORM_ADMIN;
  const isTenantAdmin = currentUser?.role === UserRole.ADMIN || isPlatformAdmin;
  const isUser = currentUser?.role === UserRole.USER;

  return (
    <AuthContext.Provider value={{ 
      currentUser, 
      loading, 
      error, 
      isAuthorizationError,
      refetch: fetchCurrentUser,
      isPlatformAdmin,
      isTenantAdmin,
      isUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
